import { useEffect, useState } from "react";
import { Megaphone, Sparkles, Wrench, TrendingUp, Plus, Trash2 } from "lucide-react";
import { api, useAuth, formatApiError } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";

export const ANNONCE_TYPES = {
  nouveaute: { label: "Nouveauté", classes: "bg-emerald-950 text-emerald-400 border-emerald-700", icon: Sparkles },
  amelioration: { label: "Amélioration", classes: "bg-blue-950 text-blue-400 border-blue-700", icon: TrendingUp },
  correctif: { label: "Correctif", classes: "bg-amber-950 text-amber-400 border-amber-700", icon: Wrench },
};

export function AnnonceTypeBadge({ type }) {
  const t = ANNONCE_TYPES[type] || ANNONCE_TYPES.nouveaute;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${t.classes}`} data-testid={`annonce-type-${type}`}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

export default function MisesAJour() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ titre: "", description: "", type: "nouveaute" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/annonces").then((r) => setAnnonces(r.data.annonces))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const publier = async () => {
    setSaving(true);
    try {
      await api.post("/annonces", form);
      toast.success("Annonce publiée — elle sera proposée à tous à leur prochaine connexion");
      setForm({ titre: "", description: "", type: "nouveaute" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const supprimer = async (a) => {
    if (!window.confirm(`Supprimer l'annonce « ${a.titre} » ?`)) return;
    try {
      await api.delete(`/annonces/${a.id}`);
      toast.success("Annonce supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="mises-a-jour-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Nouveautés du site</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
          <Megaphone className="h-8 w-8 text-accent" /> Mises à jour
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Historique des nouvelles fonctionnalités, améliorations et correctifs du site.</p>
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="annonce-form">
          <h2 className="text-base font-bold">Publier une annonce</h2>
          <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex. Nouvelle fonctionnalité..." className="bg-background" data-testid="annonce-form-titre" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="bg-background" data-testid="annonce-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nouveaute">Nouveauté</SelectItem>
                  <SelectItem value="amelioration">Amélioration</SelectItem>
                  <SelectItem value="correctif">Correctif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Détails visibles par tous les utilisateurs..." className="bg-background" data-testid="annonce-form-description" />
          </div>
          <div className="flex justify-end">
            <Button onClick={publier} disabled={saving || form.titre.trim().length < 2} data-testid="annonce-form-submit">
              <Plus className="h-4 w-4 mr-2" /> {saving ? "Publication..." : "Publier"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="annonces-list">
        {loading && <p className="py-8 text-center text-muted-foreground">Chargement...</p>}
        {!loading && annonces.length === 0 && <p className="py-8 text-center text-muted-foreground" data-testid="annonces-empty">Aucune annonce pour le moment.</p>}
        {!loading && annonces.map((a) => (
          <div key={a.id} className="rounded-xl border border-border bg-card p-4 space-y-1.5" data-testid={`annonce-card-${a.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <AnnonceTypeBadge type={a.type} />
              <span className="text-xs font-mono text-muted-foreground">{a.date}</span>
              {a.version && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-border text-muted-foreground">v{a.version}</span>}
              {isAdmin && (
                <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto hover:text-red-400" onClick={() => supprimer(a)} data-testid={`annonce-delete-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <p className="font-semibold">{a.titre}</p>
            {a.description && <p className="text-sm text-muted-foreground leading-relaxed">{a.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
